/* Turn a DRF error payload into lines a player can read.

   Six modals each rendered `{key}: {value}` over the payload, which prints "detail: Lobby is full"
   — `detail` is not a field, it is DRF's envelope for "something about the request as a whole", so
   naming it tells the player nothing and reads like a leaked variable. Field errors are the
   opposite: "password: this field is required" is exactly what they need.

   Arrays matter too: DRF gives field errors as lists, and joining them is the difference between
   one readable line and a rendered array. */

const FIELDLESS = new Set(["detail", "non_field_errors", "error", "message"])

/* And `code` is not a message at all. This codebase's refusals answer `{detail, code}` — the
   sentence and the exception's class name — so a player who typed a lobby number nobody is using
   read "Lobby not found" and, on the line under it, "code: LobbyNotFoundError". The same reasoning
   the file already applies to `detail`'s *key*, applied to `code`'s value: it is plumbing. The
   client has `playerFacingError` for turning a code into words where it wants to; printing the
   class name is never the answer. */
const NOT_FOR_READING = new Set(["code"])

const asText = (value) => (Array.isArray(value) ? value.join(" ") : String(value))

/* `except` names the fields a form prints beside their own input (`fieldError`), so they are not
   printed twice. */
export const apiErrorLines = (errors, { except = [] } = {}) => {
  if (!errors) return []
  if (typeof errors === "string") return [{ key: "detail", text: errors }]

  return Object.entries(errors)
    .filter(([key]) => !NOT_FOR_READING.has(key) && !except.includes(key))
    .map(([key, value]) => ({
      key,
      // A field error keeps its field; an envelope key is dropped, because the sentence is the
      // whole message and the key is DRF's plumbing.
      text: FIELDLESS.has(key) ? asText(value) : `${key}: ${asText(value)}`,
    }))
}

/* One field's refusal, as one sentence, for the line under its input: registration and the reset
   answer a weak password with 400 `{"password": ["This password is too short. …", …]}` (gamekit
   0.11.0, Django's validators), and the player reads it where they typed it. Null when the field
   was not refused. */
export const fieldError = (errors, field) => {
  if (!errors || typeof errors !== "object" || Array.isArray(errors)) return null
  const value = errors[field]
  if (value == null || value === "") return null
  return asText(value)
}

/* How long a throttled request (429) says to wait, in whole seconds, or null. DRF sends it as the
   `Retry-After` header and inside its English `detail` ("Expected available in 42 seconds."); the
   header first, the sentence as a fallback, so the client can say it in the player's language. */
export const retryAfterSeconds = (response) => {
  const header = response?.headers?.["retry-after"]
  const fromHeader = Number.parseInt(header, 10)
  if (Number.isFinite(fromHeader) && fromHeader >= 0) return fromHeader
  const match = /(\d+)\s*second/.exec(String(response?.data?.detail ?? ""))
  return match ? Number.parseInt(match[1], 10) : null
}

export default apiErrorLines
